import React from 'react';
import { Alert, Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { localeDirection } from '@modules/shared-locales';
import type {
  CreativeReportEnrichment,
  CreativeReportEnrichmentItem,
  CreativeReportEnrichmentMedia,
} from '../../creativeDecisions';
import {
  buildCreativeRenditionReportHref,
  buildCreativeRenditionVariationHref,
} from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';

export function CreativeReportEnrichmentThumbnail({
  item,
}: {
  item: CreativeReportEnrichmentItem | undefined;
}): React.ReactElement | null {
  if (!item?.enrichment) return null;
  return (
    <CreativeMedia
      enrichment={item.enrichment}
      compact
      testId={CREATIVE_DECISIONS_TEST_IDS.enrichmentThumbnail(item.deploymentKey)}
    />
  );
}

export function CreativeReportEnrichmentPanel({
  item,
  loading = false,
}: {
  item: CreativeReportEnrichmentItem | undefined;
  loading?: boolean;
}): React.ReactElement | null {
  if (!item) {
    if (!loading) return null;
    return (
      <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentLoading}>
        Loading the exact exported creative… Performance evidence remains available below.
      </Alert>
    );
  }
  if (!item.enrichment) {
    return (
      <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentUnavailable}>
        {`The exact Segmently export is unavailable (${humanizeReason(item.reason)}). Performance evidence remains available below.`}
      </Alert>
    );
  }
  const { binding, manifest } = item.enrichment;
  const exactRenditionHref = buildCreativeRenditionReportHref(item.enrichment);
  const startVariationHref = buildCreativeRenditionVariationHref(item.enrichment);
  return (
    <Box
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerEnrichment}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>Exact exported creative</Typography>
          <Typography variant="caption" color="text.secondary">
            Persisted rendition that produced this provider deployment
          </Typography>
        </Box>
        <Chip size="small" label={manifest.locale} />
      </Stack>
      <CreativeMedia enrichment={item.enrichment} compact={false} />
      <Stack
        spacing={0.75}
        sx={{ mt: 1.25 }}
        lang={manifest.locale}
        dir={localeDirection(manifest.locale)}
      >
        {manifest.copy.headline && <CopyLine label="Headline" value={manifest.copy.headline} />}
        {manifest.copy.primaryText && <CopyLine label="Primary text" value={manifest.copy.primaryText} />}
        {manifest.copy.description && <CopyLine label="Description" value={manifest.copy.description} />}
        {manifest.copy.cta && <CopyLine label="CTA" value={manifest.copy.cta} />}
        {manifest.copy.overlayText && <CopyLine label="On-media text" value={manifest.copy.overlayText} />}
        {manifest.copy.subtitleScript && <CopyLine label="Subtitle / spoken script" value={manifest.copy.subtitleScript} />}
        {(manifest.copy.cardCopy?.length ?? 0) > 0 && (
          <Box component="section" aria-label="Localized carousel card copy">
            <Typography variant="caption" color="text.secondary" display="block">Carousel cards</Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {manifest.copy.cardCopy!.map((card, index) => (
                <Box key={card.cardKey ?? index} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.75 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {card.cardKey ?? `Card ${index + 1}`}
                  </Typography>
                  {card.headline && <CopyLine label="Headline" value={card.headline} />}
                  {card.body && <CopyLine label="Body" value={card.body} />}
                  {card.cta && <CopyLine label="CTA" value={card.cta} />}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        {manifest.destination.url && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Destination</Typography>
            <Link href={manifest.destination.url} target="_blank" rel="noopener noreferrer" sx={{ overflowWrap: 'anywhere' }}>
              {manifest.destination.label ?? manifest.destination.url}
            </Link>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {`Rendition ${manifest.renditionId} · provider payload ${binding.providerPayloadVersion}`}
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1.5 }}>
        <Link href={exactRenditionHref} underline="hover">
          Open exact rendition
        </Link>
        <Button
          component="a"
          href={startVariationHref}
          variant="outlined"
          size="small"
          endIcon={<OpenInNewIcon />}
        >
          Start variation from this rendition
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        The handoff creates a new editable Segmently copy. It does not edit the provider ad or its source snapshot.
      </Typography>
    </Box>
  );
}

function CreativeMedia({
  enrichment,
  compact,
  testId,
}: {
  enrichment: CreativeReportEnrichment;
  compact: boolean;
  testId?: string;
}): React.ReactElement | null {
  const allMedia = enrichment.manifest.media;
  if (allMedia.length === 0) return null;
  const media = compact
    ? allMedia.find((entry) => entry.mediaKind === 'image' || entry.mediaKind === 'video' || entry.mediaKind === 'poster') ?? allMedia[0]
    : null;
  if (compact && media) {
    return (
      <CreativeMediaItem
        media={media}
        locale={enrichment.manifest.locale}
        headline={enrichment.manifest.copy.headline}
        compact
        testId={testId}
      />
    );
  }
  return (
    <Stack spacing={1} component="section" aria-label="Exact exported media">
      {allMedia.map((entry, index) => (
        <Box key={`${entry.slotKey}:${entry.assetId}`} sx={{ minWidth: 0 }}>
          {allMedia.length > 1 && (
            <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" label={entry.slotKey} />
              <Chip size="small" variant="outlined" label={entry.mediaKind.replaceAll('_', ' ')} />
            </Stack>
          )}
          <CreativeMediaItem
            media={entry}
            locale={enrichment.manifest.locale}
            headline={enrichment.manifest.copy.headline}
            compact={false}
            testId={index === 0
              ? CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentMedia
              : CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentMediaAt(index)}
          />
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            {entry.captions && <Chip size="small" color="success" variant="outlined" label={`${entry.captions.locale} captions · ${entry.captions.cueCount} cues`} />}
            {entry.audioTrackAssetId && <Chip size="small" variant="outlined" label="Localized audio attached" />}
            {entry.posterAssetId && <Chip size="small" variant="outlined" label="Exact poster attached" />}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function CreativeMediaItem({
  media,
  locale,
  headline,
  compact,
  testId,
}: {
  media: CreativeReportEnrichmentMedia;
  locale: string;
  headline?: string;
  compact: boolean;
  testId?: string;
}): React.ReactElement {
  const mediaSx: SxProps<Theme> = compact
    ? { width: 44, height: 44, borderRadius: 0.75, objectFit: 'cover', bgcolor: 'action.hover', flexShrink: 0 }
    : { width: '100%', maxHeight: 320, borderRadius: 1, objectFit: 'contain', bgcolor: 'action.hover' };
  if (media.mediaKind === 'video') {
    return (
      <Box
        component="video"
        src={media.url}
        poster={media.smallUrl}
        crossOrigin="anonymous"
        preload="metadata"
        controls={!compact}
        muted={compact}
        playsInline
        aria-label="Localized creative video"
        data-testid={testId ?? CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentMedia}
        sx={mediaSx}
      >
        {!compact && media.captions && (
          <track
            kind="subtitles"
            src={media.captions.url}
            srcLang={media.captions.locale}
            label={`${media.captions.locale} subtitles`}
            default
          />
        )}
      </Box>
    );
  }
  if (media.mediaKind === 'audio_track') {
    return (
      <Box
        component="audio"
        src={media.url}
        preload="metadata"
        controls={!compact}
        aria-label={`Localized creative audio · ${media.slotKey}`}
        data-testid={testId ?? CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentMedia}
        sx={compact ? { width: 44 } : { width: '100%' }}
      />
    );
  }
  if (media.mediaKind === 'captions' || media.mediaKind === 'transcript') {
    return (
      <Link
        href={media.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId ?? CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentMedia}
      >
        Open exact {media.mediaKind}
      </Link>
    );
  }
  return (
    <Box
      component="img"
      src={compact ? media.smallUrl ?? media.url : media.url}
      loading="lazy"
      alt={headline ?? `Localized creative · ${locale}`}
      data-testid={testId ?? CREATIVE_DECISIONS_TEST_IDS.drawerEnrichmentMedia}
      sx={mediaSx}
    />
  );
}

function CopyLine({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
    </Box>
  );
}

function humanizeReason(reason: CreativeReportEnrichmentItem['reason']): string {
  return (reason ?? 'unknown').replaceAll('_', ' ');
}
